import React, { useMemo, useState } from "react"
import { ChevronDown } from "lucide-react"
import { GROUP_SIZE_BUCKETS, UNKNOWN_BUCKET, groupByBucket } from "../_utils/buckets"
import { rate, formatRate } from "../_utils/data"

// Conversion broken down by group-size bucket. Each bucket is an accordion row;
// expanding it reveals the per-agent split inside that bucket. Numerator
// (bookings) and denominator (enquiries) are bucketed on the same group_size
// field so the rates are like-for-like.
export default function GroupSizeConversion({ agents, dataset }) {
	const [openKeys, setOpenKeys] = useState(() => new Set())

	const buckets = useMemo(() => {
		if (!dataset) return []

		const enquiriesByBucket = groupByBucket(dataset.finalEnquiries)
		const bookingsByBucket = groupByBucket(dataset.allBookings)

		// Only surface the Unknown bucket when it actually holds something.
		const definitions = [...GROUP_SIZE_BUCKETS]
		const unknownHasData = enquiriesByBucket[UNKNOWN_BUCKET.key].length > 0 || bookingsByBucket[UNKNOWN_BUCKET.key].length > 0
		if (unknownHasData) definitions.push(UNKNOWN_BUCKET)

		return definitions.map((bucket) => {
			const bucketEnquiries = enquiriesByBucket[bucket.key]
			const bucketBookings = bookingsByBucket[bucket.key]

			const agentRows = agents
				.map((agent) => {
					const enquiries = bucketEnquiries.filter((enquiry) => enquiry.agent === agent.id).length
					const bookings = bucketBookings.filter((booking) => booking.agent === agent.id).length
					return { id: agent.id, name: agent.name, enquiries, bookings, conversion: rate(bookings, enquiries) }
				})
				.filter((row) => row.enquiries > 0 || row.bookings > 0)
				.sort((a, b) => b.bookings - a.bookings || (b.conversion || 0) - (a.conversion || 0))

			return {
				key: bucket.key,
				label: bucket.label,
				enquiries: bucketEnquiries.length,
				bookings: bucketBookings.length,
				conversion: rate(bucketBookings.length, bucketEnquiries.length),
				agentRows,
			}
		})
	}, [agents, dataset])

	const toggle = (key) => {
		setOpenKeys((prev) => {
			const next = new Set(prev)
			if (next.has(key)) next.delete(key)
			else next.add(key)
			return next
		})
	}

	if (!buckets.length) return null

	return (
		<div className="group-size-accordion">
			{buckets.map((bucket) => {
				const isOpen = openKeys.has(bucket.key)

				return (
					<div
						key={bucket.key}
						className={["group-size-bucket", isOpen ? "is-open" : ""].join(" ").trim()}>
						<button
							type="button"
							className="bucket-header"
							aria-expanded={isOpen}
							onClick={() => toggle(bucket.key)}>
							<span className="bucket-chevron">
								<ChevronDown size={18} />
							</span>
							<span className="bucket-title">{bucket.label} golfers</span>
							<span className="bucket-stats">
								<span>
									<em>{bucket.enquiries}</em> enquiries
								</span>
								<span>
									<em>{bucket.bookings}</em> bookings
								</span>
								<span className="bucket-conversion">{formatRate(bucket.conversion)}</span>
							</span>
						</button>

						{isOpen && (
							<div className="bucket-body">
								{bucket.agentRows.length ? (
									<table className="ui-table bucket-agent-table">
										<thead>
											<tr>
												<th>Sales agent</th>
												<th>Enquiries</th>
												<th>Bookings</th>
												<th>Conversion</th>
											</tr>
										</thead>
										<tbody>
											{bucket.agentRows.map((row) => (
												<tr key={row.id}>
													<td>{row.name}</td>
													<td>{row.enquiries}</td>
													<td>{row.bookings}</td>
													<td className="emphasis-cell">{formatRate(row.conversion)}</td>
												</tr>
											))}
										</tbody>
									</table>
								) : (
									<p className="bucket-empty">No agent activity in this group size for the selected period.</p>
								)}
							</div>
						)}
					</div>
				)
			})}
		</div>
	)
}
